import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Row, Col, message } from "antd";
import { HEADERS_EXPORT_BEN, POST_ADD_ADDRESS_TR } from "../../../API/apiUrls";
import axios from "axios";

const AddAddressDetail = ({
  open,
  close,
  currentPerson,
  geoRawData,
  func,
  editingAddress,
}) => {
  const [form] = Form.useForm();
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSubDistrict, setSelectedSubDistrict] = useState(null);
  const [loadding, setLoading] = useState(false);

  // Reset form เมื่อเปิด Modal
  React.useEffect(() => {
    if (open) {
      if (editingAddress) {
        // ✅ หา provCode จาก provDesc (PROVDES)
        const province = geoRawData?.province?.find(
          (p) => p.provDesc === editingAddress.PROVDES
        );
        const provinceCode = province ? String(province.provCode) : null;

        // ✅ หา distCode จาก distDesc (AUMPDES)
        const district = geoRawData?.district?.find(
          (d) =>
            d.distDesc === editingAddress.AUMPDES &&
            String(d.provCode) === provinceCode
        );
        const districtCode = district ? String(district.distCode) : null;

        // ✅ หา subDistCode จาก subDistDesc (TUMB)
        const subDistrict = geoRawData?.subDistrict?.find(
          (s) =>
            s.subDistDesc === editingAddress.TUMB &&
            String(s.distCode) === districtCode &&
            String(s.provCode) === provinceCode
        );
        const subDistrictCode = subDistrict
          ? String(subDistrict.subDistCode)
          : null;

        console.log("Edit Mode - Codes:", {
          provinceCode,
          districtCode,
          subDistrictCode,
        });

        // Set state
        setSelectedProvince(provinceCode);
        setSelectedDistrict(districtCode);
        setSelectedSubDistrict(subDistrictCode);

        // Set form values
        form.setFieldsValue({
          ADDRNO: editingAddress.ADDRNO,
          ADDR1: editingAddress.ADDR1,
          ADDR2: editingAddress.ADDR2 || null,
          PROVDES: provinceCode,
          AUMPDES: districtCode,
          TUMB: subDistrictCode,
          ZIP: editingAddress.ZIP,
        });
      } else {
        // Reset ทุกอย่างเมื่อเป็น add mode
        form.resetFields();
        form.setFieldsValue({ ADDRNO: 4 });
        setSelectedProvince(null);
        setSelectedDistrict(null);
        setSelectedSubDistrict(null);
      }
    }
  }, [open, editingAddress, form, geoRawData]);

  // ✅ จังหวัดทั้งหมด
  const provinceOptions = useMemo(() => {
    if (!geoRawData?.province) return [];
    return geoRawData.province.map((prov) => ({
      label: prov.provDesc,
      value: String(prov.provCode),
      data: prov,
    }));
  }, [geoRawData]);

  // ✅ อำเภอ - filter ตามจังหวัดที่เลือกเท่านั้น
  const districtOptions = useMemo(() => {
    if (!geoRawData?.district || !selectedProvince) return [];

    const filteredDistricts = geoRawData.district.filter(
      (dist) => String(dist.provCode) === String(selectedProvince)
    );

    return filteredDistricts.map((dist, index) => ({
      label: dist.distDesc,
      value: String(dist.distCode),
      data: {
        ...dist,
        provCode: String(dist.provCode),
        distCode: String(dist.distCode),
      },
      key: `dist-${dist.provCode}-${dist.distCode}-${index}`,
    }));
  }, [geoRawData, selectedProvince]);

  // ✅ ตำบล - filter ตามอำเภอที่เลือกเท่านั้น
  const subDistrictOptions = useMemo(() => {
    if (!geoRawData?.subDistrict || !selectedDistrict) return [];

    const filteredSubDistricts = geoRawData.subDistrict.filter(
      (sub) =>
        String(sub.provCode) === String(selectedProvince) &&
        String(sub.distCode) === String(selectedDistrict)
    );

    return filteredSubDistricts.map((sub, index) => ({
      label: sub.subDistDesc,
      value: String(sub.subDistCode),
      data: {
        ...sub,
        provCode: String(sub.provCode),
        distCode: String(sub.distCode),
        subDistCode: String(sub.subDistCode),
      },
      key: `sub-${sub.provCode}-${sub.distCode}-${sub.subDistCode}-${index}`,
    }));
  }, [geoRawData, selectedProvince, selectedDistrict]);

  // ✅ Handler: เลือกจังหวัด
  const handleProvinceChange = (value) => {
    setSelectedProvince(value);
    setSelectedDistrict(null);
    setSelectedSubDistrict(null);

    // Reset อำเภอ, ตำบล และรหัสไปรษณีย์
    form.setFieldsValue({
      AUMPDES: undefined,
      TUMB: undefined,
      ZIP: "",
    });
  };

  // ✅ Handler: เลือกอำเภอ
  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedSubDistrict(null);

    // Reset ตำบลและรหัสไปรษณีย์
    form.setFieldsValue({
      TUMB: undefined,
      ZIP: "",
    });
  };

  // ✅ Handler: เลือกตำบล - Auto-fill รหัสไปรษณีย์
  const handleSubDistrictChange = (value, option) => {
    if (!option) {
      setSelectedSubDistrict(null);
      form.setFieldsValue({ ZIP: "" });
      return;
    }

    setSelectedSubDistrict(value);

    // ✅ Auto-fill รหัสไปรษณีย์
    if (option?.data?.zipcode) {
      form.setFieldsValue({
        ZIP: option.data.zipcode,
      });
    }
  };

  // ✅ บันทึกข้อมูล
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // ✅ จัดข้อมูลตาม format ที่ API ต้องการ
      const addressTrData = {
        addrTrId: 0, // 0 = เพิ่มใหม่
        customerId: currentPerson?.CUSTOMER_ID || null,
        houseRegistrationNumber: values.houseRegistrationNumber || null,
        houseNumber: values.ADDR1 || null,
        soi: values.SOI || null,
        road: values.ADDR2 || null,
        village: values.MOOBAN || null,
        province: values.PROVDES || null,
        district: values.AUMPDES || null,
        subdistrict: values.TUMB || null,
        zipcode: values.ZIP || null,
      };

      console.log("💾 Saving address:", addressTrData);

      // ⭐ เรียก API
      const result = await postAddAddressTr(addressTrData);

      if (result) {
        // ⭐ ส่งข้อมูลกลับไปยัง parent component

        func({
          action: "refresh", // บอกให้ parent refresh ข้อมูล
          data: result,
        });

        // ปิด modal
        close();
      }
    } catch (error) {
      console.error("❌ Validation failed:", error);
      message.error("กรุณากรอกข้อมูลให้ครบถ้วน");
    }
  };

  // ✅ ฟังก์ชันส่งข้อมูลไป API
  const postAddAddressTr = async (rawData) => {
    console.log("📤 POST_ADD_ADDRESS_TR with data:", rawData);

    setLoading(true);
    try {
      const res = await axios.post(POST_ADD_ADDRESS_TR, [rawData], {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        message.success("บันทึกที่อยู่ ทร.14 สำเร็จ");
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`
        );
      }

      console.error("❌ postAddAddressTr error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fullName = (p) =>
    p ? [p?.SNAM, p?.NAME1, p?.NAME2].filter(Boolean).join(" ") : "";

  const handleCancel = () => {
    close();
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: 17 }}>
          {editingAddress
            ? `แก้ไขที่อยู่ - ${fullName(currentPerson)}`
            : `เพิ่มที่อยู่ตาม ทร.14 - ${fullName(currentPerson)}`}
        </span>
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleSave}
      width={900}
      okText={<span style={{ fontSize: 15 }}>บันทึก</span>}
      cancelText={<span style={{ fontSize: 15 }}>ยกเลิก</span>}
    >
      <Form form={form} layout="vertical" initialValues={{ ADDRNO: 4 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ADDRNO"
              label={<span style={{ fontSize: 14 }}>ประเภทที่อยู่</span>}
              rules={[{ required: true, message: "กรุณาเลือกประเภทที่อยู่" }]}
            >
              <Select placeholder="เลือกประเภทที่อยู่" size="large" disabled>
                <Select.Option value={1}>ทะเบียนบ้าน</Select.Option>
                <Select.Option value={2}>ส่งเอกสาร</Select.Option>
                <Select.Option value={3}>ที่ทำงาน</Select.Option>
                <Select.Option value={4}>ทะเบียนราษฎร</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="ADDR1"
          label={<span style={{ fontSize: 14 }}>ที่อยู่ บรรทัดที่ 1</span>}
          rules={[{ required: true, message: "กรุณากรอกที่อยู่" }]}
        >
          <Input placeholder="เลขที่, หมู่, ซอย" size="large" />
        </Form.Item>

        <Form.Item
          name="ADDR2"
          label={<span style={{ fontSize: 14 }}>ที่อยู่ บรรทัดที่ 2</span>}
        >
          <Input placeholder="ถนน, หมู่บ้าน" size="large" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="PROVDES"
              label={<span style={{ fontSize: 14 }}>จังหวัด</span>}
              rules={[{ required: true, message: "กรุณาเลือกจังหวัด" }]}
            >
              <Select
                showSearch
                placeholder="เลือกจังหวัด"
                optionFilterProp="label"
                options={provinceOptions}
                onChange={handleProvinceChange}
                allowClear
                loading={!geoRawData}
                disabled={!geoRawData}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="AUMPDES"
              label={<span style={{ fontSize: 14 }}>อำเภอ/เขต</span>}
              rules={[{ required: true, message: "กรุณาเลือกอำเภอ" }]}
            >
              <Select
                showSearch
                placeholder={
                  selectedProvince ? "เลือกอำเภอ/เขต" : "กรุณาเลือกจังหวัดก่อน"
                }
                optionFilterProp="label"
                options={districtOptions}
                onChange={handleDistrictChange}
                allowClear
                disabled={!selectedProvince}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="TUMB"
              label={<span style={{ fontSize: 14 }}>ตำบล/แขวง</span>}
              rules={[{ required: true, message: "กรุณาเลือกตำบล" }]}
            >
              <Select
                showSearch
                placeholder={
                  selectedDistrict ? "เลือกตำบล/แขวง" : "กรุณาเลือกอำเภอก่อน"
                }
                optionFilterProp="label"
                options={subDistrictOptions}
                onChange={handleSubDistrictChange}
                allowClear
                disabled={!selectedDistrict}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ZIP"
              label={<span style={{ fontSize: 14 }}>รหัสไปรษณีย์</span>}
              rules={[
                { required: true, message: "กรุณากรอกรหัสไปรษณีย์" },
                {
                  pattern: /^[0-9]{5}$/,
                  message: "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก",
                },
              ]}
            >
              <Input
                placeholder="รหัสไปรษณีย์ 5 หลัก"
                maxLength={5}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddAddressDetail;
